<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
    :fullscreen="$vuetify.display.smAndDown"
    scrollable
  >
    <v-card>
      <v-card-title class="redColor parts-selection-title d-flex align-center ga-2">
        <span
          class="text-subtitle-1 font-weight-medium text-white flex-grow-1 parts-selection-title-text"
        >
          {{ $t("vehicle_entry.select_parts") }}
        </span>
        <v-btn
          variant="outlined"
          icon
          color="white"
          class="parts-selection-close-btn flex-shrink-0"
          size="small"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="flex-wrap">
          <!------------------------------------- CAR DETAILS ------------------------------------->
          <v-col cols="12" class="mt-3">
            <v-row v-if="version" class="flex-wrap">
              <v-col cols="12" md="5">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.version")
                  }}</span>
                  <span class="font-weight-bold">{{
                    version.description
                  }}</span>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" md="2">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.year")
                  }}</span>
                  <span class="font-weight-bold">
                    {{ formatMonthYear(version.startingDate) }} -
                    {{ formatMonthYear(version.endingDate) }}
                  </span>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.body")
                  }}</span>
                  <span class="font-weight-bold">{{
                    version.numberOfDoors
                  }}</span>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.power")
                  }}</span>
                  <span class="font-weight-bold">
                    {{ version.power.kw }} kw,
                  </span>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.engine")
                  }}</span>
                  <span class="font-weight-bold">{{ version.engineCode }}</span>
                </v-row>
              </v-col>
              <v-col cols="12" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.supply.supply")
                  }}</span>
                  <span class="font-weight-bold">{{
                    getSupplyLocale(version.supplyCode)
                  }}</span>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <!---------------------------------------- LIST ----------------------------------------->
          <v-col cols="12" class="mt-2">
            <v-row v-if="loading" align="center" justify="center">
              <v-progress-circular :size="50" color="red" indeterminate />
            </v-row>

            <div v-else class="parts-selection-list">
              <div
                v-for="item in items"
                :key="item._id"
                class="parts-selection-item"
              >
                <v-divider />
                <v-row class="flex-wrap align-center ma-0">
                  <v-col cols="2" sm="1" class="py-0 pl-0 flex-shrink-0">
                    <v-checkbox
                      v-model="item.selected"
                      class="mt-3"
                      color="red-darken-1"
                      hide-details
                    />
                  </v-col>

                  <v-col cols="10" sm="11" md="5" class="py-0 min-width-0">
                    <div class="mt-2 mt-sm-4">
                      {{ item.title[$store.getters.language] || item.title.it }}
                    </div>
                  </v-col>

                  <v-col cols="12" md="6" class="py-0">
                    <v-text-field
                      v-model="item.notes"
                      :label="$t('vehicle_entry.notes')"
                      color="red-darken-1"
                      hide-details
                    />
                  </v-col>
                </v-row>
              </div>
            </div>
            <!--Comment-->
            <v-textarea
              v-model="comment"
              :label="$t('vehicle_entry.comment')"
              color="red-darken-1"
              class="mt-2"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="parts-selection-actions pa-3">
        <v-spacer class="d-none d-md-flex" />
        <v-btn
          color="blue darken-1"
          :disabled="loading"
          variant="outlined"
          @click="save"
        >
          {{ $t("vehicle_entry.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";

import getRequest from "../../api/getRequest";

export default {
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },

  data: () => ({
    dialog: false,
    loading: false,
    version: null,
    position: null,
    items: [],
    comment: null,
  }),
  methods: {
    open(position, vehicle, edit) {
      this.dialog = true;
      this.loading = true;
      this.position = position;
      this.version =
        vehicle && vehicle.details && vehicle.details.version
          ? vehicle.details.version
          : null;

      this.items =
        vehicle.parts && vehicle.parts[position] ? vehicle.parts[position] : [];
      if (vehicle.parts && vehicle.parts.comment)
        this.comment = vehicle.parts.comment;

      getRequest("/part-groups/", `position=${position}`)
        .then((response) => {
          if (response) {
            const parts = response.parts || [];
            const newItems = parts.reduce((accumulator, part) => {
              if (!this.items.find((i) => i.partCode === part.partCode)) {
                const _part = part;
                if (edit && edit === true)
                  _part.selected = false; // Edit dialog
                else _part.selected = true;
                accumulator.push(_part);
              }
              return accumulator;
            }, []);

            this.items = this.items.concat(newItems);
          }
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
    },
    close() {
      this.dialog = false;
      this.position = null;
      this.items = [];
      this.comment = null;
      this.$emit("close");
    },
    save() {
      this.$emit("save", {
        position: this.position,
        items: this.items,
        comment: this.comment,
      });
      this.close();
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.parts-selection-title {
  padding: 12px 16px;
}

.parts-selection-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

.parts-selection-list {
  overflow-x: hidden;
}

.parts-selection-list .min-width-0 {
  min-width: 0;
  word-break: break-word;
}

@media (max-width: 959.98px) {
  .parts-selection-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .parts-selection-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }

  .parts-selection-list :deep(.v-field) {
    min-width: 0;
  }

  .parts-selection-actions {
    justify-content: center;
  }

  .parts-selection-actions .v-btn {
    width: 100%;
    max-width: 16rem;
  }
}
</style>
